import { useCallback, useState } from "react";

import {
  assignedWorkspaceIds,
  resolveDefaultWorkspace,
} from "../utils/defaultWorkspace";

/**
 * The default-workspace choice in a provisioning modal.
 *
 * `default_id_client` is the workspace a new user lands on at login. An admin
 * adds, removes and re-points assignment rows freely, so the choice has two
 * halves: the admin's pick, and what the modal submits when that pick names no
 * assigned row.
 *
 * `effectiveDefault` is derived on every render rather than repaired in an
 * effect, so the submitted value is always a workspace the user was granted.
 * `discardIfUnassigned` covers the other half: a pick that none of the rows
 * names is dropped, so bringing that workspace back leaves the default alone
 * instead of restoring the pick with no click.
 *
 * Call `discardIfUnassigned` with the rows that remain, since the caller knows
 * the next state before React does.
 */
export function useDefaultWorkspace(assignments) {
  const [picked, setPicked] = useState(null);

  // Stable identities: both modals reset the pick from their open-effect, and
  // a callback rebuilt each render would either re-run that effect every render
  // or have to be omitted from its dependency array.
  const reset = useCallback(() => setPicked(null), []);
  const discardIfUnassigned = useCallback(
    (remaining) => {
      if (picked && !assignedWorkspaceIds(remaining).includes(picked)) {
        setPicked(null);
      }
    },
    [picked]
  );

  return {
    assignedWorkspaces: assignedWorkspaceIds(assignments),
    effectiveDefault: resolveDefaultWorkspace(assignments, picked),
    pick: setPicked,
    reset,
    discardIfUnassigned,
  };
}
